import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { Annotation, ChangeSet, Compartment, EditorSelection, EditorState, Prec, Transaction, type Extension } from '@codemirror/state'
import {
  EditorView, keymap, lineNumbers, highlightActiveLine, highlightActiveLineGutter, highlightSpecialChars,
  drawSelection, dropCursor, rectangularSelection, crosshairCursor, type Command,
} from '@codemirror/view'
import { defaultKeymap, indentLess, indentMore } from '@codemirror/commands'
import { bracketMatching, foldGutter, foldKeymap, getIndentUnit, indentOnInput } from '@codemirror/language'
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete'
import { search, searchKeymap, highlightSelectionMatches } from '@codemirror/search'
import { useTranslation } from '@papan/i18n'
import { useToastOptional } from '@papan/core-ui/ui'
import { runFormat } from '../format/format-runner.js'
import { formatHunks } from '../format/format-hunks.js'
import type { EditorTextChange, TextModifierHandle, TextModifierProps, TextSurface } from '../text-modifier-types.js'
import { editorTheme, editorHighlight } from './theme.js'
import { builtinLanguage, loadLanguage } from './languages.js'
import { intelExtensions, type IntelEnv } from './intel.js'
import { revealLine as revealLineIn, revealLineExtension } from './reveal-line.js'
import { toLf } from '../write-editor-change.js'

/**
 * The source editor on CodeMirror 6, behind the hosts' editor contract
 * (text-modifier-types.ts) — the hosts own the Yjs document, undo, save and
 * conflict handling:
 *
 *  - Local edits are reported through `onChange` as the whole new text plus the
 *    edit's own changes (`EditorTextChange`), which the host writes into its
 *    Y.Text as they are (write-editor-change.ts). CodeMirror keeps no undo history of its own: undo /
 *    redo keys go to the host's `onKeyDown`, which undoes the shared document.
 *  - A remote change (the host's Y.Text moved) arrives as a new `value`; it is
 *    applied as a minimal diff, so the caret and selection stay put.
 *  - `value` lags the document by a frame (useCollabText snapshots in a rAF), so
 *    sync compares against `readLatestValue()` when given, never the snapshot —
 *    a snapshot older than the last keystroke would undo it.
 *  - The handle's `textarea` is a `TextSurface` over the view (value, selection,
 *    scroll, focus) for the hosts' clipboard and caret code.
 */

/** Marks a transaction that carries the host's document into the editor (not an edit to report). */
const fromHost = Annotation.define<boolean>()

/** The smallest change turning `a` into `b` (common prefix and suffix kept). */
export function minimalChange(a: string, b: string): { from: number; to: number; insert: string } {
  let start = 0
  const max = Math.min(a.length, b.length)
  while (start < max && a.charCodeAt(start) === b.charCodeAt(start)) start++
  let endA = a.length
  let endB = b.length
  while (endA > start && endB > start && a.charCodeAt(endA - 1) === b.charCodeAt(endB - 1)) { endA--; endB-- }
  return { from: start, to: endA, insert: b.slice(start, endB) }
}

/**
 * #249: requests already revealed (`seq` is unique across the tab). Kept outside the editor because hosts
 * remount it — Markdown's preview ↔ source, a script's Run ↔ Src, a chat side panel switching files back —
 * while still passing the same request; a per-instance record would jump to the line again each time.
 */
const revealedSeqs = new Set<number>()

/**
 * Read-only: no edits — and no soft keyboard. CodeMirror keeps the surface contenteditable (it marks it
 * aria-readonly), so a tap on a phone would open the keyboard over a view-only file; the textarea had `readOnly`.
 */
function readOnlyExt(ro: boolean): Extension {
  return [EditorState.readOnly.of(ro), ro ? EditorView.contentAttributes.of({ inputmode: 'none' }) : []]
}

/** Whether a reveal request is still to happen — hosts switch to their source view only for one that is. */
export function revealPending(req: { seq: number } | undefined): boolean {
  return !!req && !revealedSeqs.has(req.seq)
}

/**
 * Tab as Monaco does it: at a caret, spaces up to the next indent stop, where the caret is (CodeMirror's own
 * `indentMore` indents the whole LINE from its start, wherever the caret is); a selection spanning text indents
 * its lines. Shift+Tab outdents.
 */
export const insertSoftTab: Command = (view) => {
  const { state } = view
  if (state.readOnly) return false
  if (state.selection.ranges.some((r) => !r.empty)) return indentMore(view)
  const unit = getIndentUnit(state)
  view.dispatch(state.update(state.changeByRange((range) => {
    const line = state.doc.lineAt(range.head)
    let col = 0
    for (let i = line.from; i < range.head; i++) col += state.doc.sliceString(i, i + 1) === '\t' ? state.tabSize - (col % state.tabSize) : 1
    const insert = ' '.repeat(unit - (col % unit))
    return { changes: { from: range.head, insert }, range: EditorSelection.cursor(range.head + insert.length) }
  }), { scrollIntoView: true, userEvent: 'input.indent' }))
  return true
}

export const CodeMirrorModifier = forwardRef<TextModifierHandle, TextModifierProps>(function CodeMirrorModifier(
  { value, readLatestValue, language, isActive, onChange, onKeyDown, mode, readOnly, codeIntel, revealLine, loadFormatConfig, scrollKey, scrollRef, scrollSyncTrigger, viewSync, viewSyncId, viewSyncTrigger },
  ref,
) {
  const isReadOnly = mode === 'readonly' || readOnly === true
  const parentRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)

  // Extensions are built once; they read the latest props through refs.
  const { t } = useTranslation('collab')
  const toast = useToastOptional()
  const latest = useRef({ value, readLatestValue, onChange, onKeyDown, codeIntel, language, isReadOnly, toast, t, loadFormatConfig, revealLine })
  latest.current = { value, readLatestValue, onChange, onKeyDown, codeIntel, language, isReadOnly, toast, t, loadFormatConfig, revealLine }
  const compartments = useMemo(() => ({ language: new Compartment(), readOnly: new Compartment(), intel: new Compartment() }), [])
  const intelEnv = (): IntelEnv => ({ fileName: latest.current.codeIntel?.fileName ?? '', app: latest.current.codeIntel?.app ?? null })

  /** The text the host's document holds now, with LF line breaks: CodeMirror's doc always is (it splits on
   *  `\r\n` / `\r` and hands back `\n`), so comparing raw CRLF text never matched and every sync rewrote it —
   *  a lone `\r` in that rewrite became an extra line. The first edit converts the file to LF
   *  (write-editor-change.ts), as the textarea editor did. */
  const hostText = () => toLf(latest.current.readLatestValue ? latest.current.readLatestValue() : latest.current.value)

  /** Bring the editor up to the host's document (a no-op while they agree). */
  const syncFromHost = () => {
    const view = viewRef.current
    if (!view) return
    const next = hostText()
    const cur = view.state.doc.toString()
    if (cur === next) return
    // In pieces, so the caret (and an IME composition) maps through and only moves where the host's text
    // changed: one span from the first change to the last put a caret between two remote edits at the span's
    // start. Past the diff's budget (a whole-file rewrite), one span.
    view.dispatch({
      changes: formatHunks(cur, next, { maxEditLength: 200, chars: true }) ?? [minimalChange(cur, next)],
      annotations: [fromHost.of(true), Transaction.addToHistory.of(false)],
    })
  }

  /** Formats running now: each collects the edits made while it runs (typing, a collaborator's change). */
  const formatTrackers = useRef(new Set<(changes: ChangeSet) => void>())

  /**
   * Format Document (Shift+Alt+F, or the workspace's Edit menu), #987. The
   * formatter runs in a background worker; its result goes in as small hunks in
   * ONE transaction through the normal edit path — one undo step, and
   * collaborators' carets only move where text changed. Edits made while it ran
   * are kept: the hunks are rebased over them — unless one overlaps such an
   * edit, then the format is not applied (with a note). Nothing is touched when
   * the code doesn't parse.
   */
  const formatNow = async (opts?: { quiet?: boolean }) => {
    const view = viewRef.current
    const { language: lang, isReadOnly: ro, t: tr } = latest.current
    const notify = opts?.quiet ? undefined : latest.current.toast
    if (!view || ro) return
    syncFromHost()
    const startDoc = view.state.doc
    const before = startDoc.toString()
    let concurrent = ChangeSet.empty(startDoc.length)
    const track = (changes: ChangeSet) => { concurrent = concurrent.compose(changes) }
    formatTrackers.current.add(track)
    let outcome
    try {
      const fileName = latest.current.codeIntel?.fileName || undefined
      const config = await latest.current.loadFormatConfig?.().catch(() => undefined)
      outcome = await runFormat({ text: before, language: lang, fileName, config })
    } finally {
      syncFromHost() // a collaborator's edit still waiting in the host counts as concurrent too
      formatTrackers.current.delete(track)
    }
    if (!outcome.ok) {
      notify?.warning(outcome.reason === 'unsupported'
        ? tr('format.unsupported')
        : outcome.reason === 'needsPlugin'
          ? tr('format.needsPlugin', { plugin: outcome.plugin })
        : outcome.reason === 'failed'
          ? tr(`format.failed.${outcome.cause}`)
        : outcome.reason === 'mismatch'
          ? tr('format.mismatch', { language: outcome.looksLike })
          : tr('format.parseError', { message: outcome.message }), 6000, undefined, { id: 'format-document' })
      return
    }
    if (outcome.unreadableConfig) notify?.info(tr('format.unreadableConfig', { file: outcome.unreadableConfig }), 6000, undefined, { id: 'format-config' })
    else if (outcome.ignoredPlugins) notify?.info(tr('format.ignoredPlugins', { plugins: outcome.ignoredPlugins.join(', ') }), 6000, undefined, { id: 'format-config' })
    // The editor may have turned read-only while the format ran (a conflict, a reconnect, a version preview, lost
    // access) or been replaced: then its edit would never reach the document, and the next keystroke would push it in.
    const current = viewRef.current
    if (!current || current !== view || latest.current.isReadOnly || !latest.current.onChange || outcome.hunks.length === 0) return
    const touched: Array<[number, number]> = []
    concurrent.iterChangedRanges((fromA, toA) => { touched.push([fromA, toA]) })
    // All or nothing: the hunks of one format belong together (a bracket opened on one line, closed lines later),
    // so when any of them overlaps an edit made while it ran, none is applied — half a format may not parse.
    // Edits elsewhere don't stop it.
    if (outcome.hunks.some((h) => touched.some(([a, b]) => h.from <= b && a <= h.to))) {
      notify?.info(tr('format.keptEdits'), 5000, undefined, { id: 'format-document' })
      return
    }
    current.dispatch({ changes: ChangeSet.of(outcome.hunks, startDoc.length).map(concurrent), userEvent: 'format' })
  }
  const formatRef = useRef(formatNow)
  formatRef.current = formatNow

  const surface = useMemo<TextSurface>(() => {
    const view = () => viewRef.current!
    const clamp = (n: number) => Math.max(0, Math.min(n, view().state.doc.length))
    return {
      get value() { syncFromHost(); return view().state.doc.toString() },
      get selectionStart() { return view().state.selection.main.from },
      set selectionStart(n: number) {
        syncFromHost()
        const a = clamp(n)
        view().dispatch({ selection: { anchor: a, head: Math.max(a, view().state.selection.main.to) } })
      },
      get selectionEnd() { return view().state.selection.main.to },
      set selectionEnd(n: number) {
        syncFromHost()
        const h = clamp(n)
        view().dispatch({ selection: { anchor: Math.min(view().state.selection.main.from, h), head: h } })
      },
      get scrollTop() { return view().scrollDOM.scrollTop },
      set scrollTop(n: number) { view().scrollDOM.scrollTop = n },
      focus() { view().focus() },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads the view and props through refs
  }, [])

  useImperativeHandle(ref, () => ({
    get textarea() { return viewRef.current ? surface : null },
    focusEditor() { viewRef.current?.focus() },
    hasFocus() { return !!viewRef.current?.hasFocus },
    format(opts) { return formatRef.current(opts) },
  }), [surface])

  // ── Scroll sync with the previewer / conflict overlay ─────────────────
  const readScrollTop = (): number | undefined => {
    if (viewSync && viewSyncId) return viewSync[viewSyncId]?.scrollTop as number | undefined
    if (scrollRef && scrollKey) return scrollRef[scrollKey]
    return undefined
  }
  const restoreScroll = () => {
    const top = readScrollTop()
    const view = viewRef.current
    if (top == null || !view) return
    requestAnimationFrame(() => { view.scrollDOM.scrollTop = top })
  }

  // ── The view ───────────────────────────────────────────────────────────
  useEffect(() => {
    const parent = parentRef.current
    if (!parent) return
    const view = new EditorView({
      parent,
      state: EditorState.create({
        doc: hostText(),
        extensions: [
          // The host's undo / redo (and any other key it handles) first. Tab stays ours (indent / accept).
          Prec.highest(EditorView.domEventHandlers({
            keydown(e) {
              // Format Document: Shift+Alt+F by PHYSICAL key. A keymap entry can't do it — on macOS Option+letter
              // types a character (Shift+Option+F is "Ï") and CodeMirror rightly never maps that back to "f".
              if (e.code === 'KeyF' && e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey) {
                e.preventDefault()
                void formatRef.current()
                return true
              }
              const handler = latest.current.onKeyDown
              if (!handler || e.key === 'Tab') return false
              handler({
                key: e.key, code: e.code, ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey, shiftKey: e.shiftKey,
                nativeEvent: e, currentTarget: surface, target: surface,
                preventDefault: () => e.preventDefault(), stopPropagation: () => e.stopPropagation(),
                isDefaultPrevented: () => e.defaultPrevented,
              } as unknown as React.KeyboardEvent<HTMLTextAreaElement>)
              return e.defaultPrevented
            },
          })),
          lineNumbers(),
          foldGutter(),
          highlightActiveLineGutter(),
          highlightSpecialChars(),
          drawSelection(),
          dropCursor(),
          // Alt+drag (Option on macOS) selects a column, as in Monaco.
          rectangularSelection(),
          crosshairCursor(),
          EditorState.allowMultipleSelections.of(true),
          indentOnInput(),
          bracketMatching(),
          closeBrackets(),
          highlightActiveLine(),
          revealLineExtension,
          highlightSelectionMatches(),
          search({ top: true }),
          keymap.of([...closeBracketsKeymap, ...defaultKeymap, ...searchKeymap, ...foldKeymap, { key: 'Tab', run: insertSoftTab, shift: indentLess }]),
          EditorState.tabSize.of(2),
          editorTheme,
          editorHighlight,
          compartments.language.of(builtinLanguage(language) ?? []),
          compartments.readOnly.of(readOnlyExt(isReadOnly)),
          compartments.intel.of(intelExtensions(language, intelEnv, { complete: !isReadOnly })),
          EditorView.contentAttributes.of({ 'aria-label': 'Source editor', spellcheck: 'false', autocapitalize: 'off', autocorrect: 'off' }),
          EditorView.updateListener.of((u) => {
            if (!u.docChanged) return
            for (const track of formatTrackers.current) track(u.changes)
            if (u.transactions.every((tr) => tr.annotation(fromHost))) return
            // Typing settles a reveal that waited on an EMPTY document (an empty file has no "loaded" moment): it must
            // not move the caret later. Only one the reveal effect saw and deferred — a request React hasn't yet run
            // the effect for is not touched.
            if (waitingRevealSeq.current != null) { revealedSeqs.add(waitingRevealSeq.current); waitingRevealSeq.current = null }
            const changes: EditorTextChange['edits']['changes'] = []
            u.changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => { changes.push({ from: fromA, to: toA, insert: inserted.toString() }) })
            const event = {
              target: { value: u.state.doc.toString() }, currentTarget: surface,
              edits: { oldLength: u.startState.doc.length, oldText: () => u.startState.doc.toString(), changes, isolate: u.transactions.some((tr) => tr.isUserEvent('format')) },
            } satisfies Pick<EditorTextChange, 'edits'> & { target: { value: string }; currentTarget: TextSurface }
            latest.current.onChange?.(event as unknown as EditorTextChange)
            // Catch up with the host before the next keystroke can arrive: when the edit was rebased over a
            // collaborator's change the editor hadn't drawn yet, the host's text is not the editor's, and a second
            // keystroke on the editor's stale text could only be placed by guessing. (A microtask: an update
            // listener must not dispatch; input events are tasks, so none slips in between.)
            if (latest.current.readLatestValue) queueMicrotask(syncFromHost)
          }),
        ],
      }),
    })
    viewRef.current = view
    const onScroll = () => {
      const top = view.scrollDOM.scrollTop
      const left = view.scrollDOM.scrollLeft
      if (viewSync && viewSyncId) {
        viewSync[viewSyncId] ??= {}
        viewSync[viewSyncId].scrollTop = top
        viewSync[viewSyncId].scrollLeft = left
      }
      if (scrollRef && scrollKey) scrollRef[scrollKey] = top
    }
    view.scrollDOM.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      view.scrollDOM.removeEventListener('scroll', onScroll)
      view.destroy()
      viewRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- built once; changing props reconfigure compartments below
  }, [])

  // The host's document moved (or a snapshot arrived): follow it.
  useEffect(() => {
    syncFromHost()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the snapshot moves; reads the latest text then
  }, [value])

  // Language: built-ins now, the rest from language-data when first needed.
  useEffect(() => {
    const view = viewRef.current
    if (!view) return
    const now = builtinLanguage(language)
    view.dispatch({ effects: compartments.language.reconfigure(now ?? []) })
    if (now) return
    let cancelled = false
    void loadLanguage(language).then((ext) => {
      if (!cancelled && ext && viewRef.current) viewRef.current.dispatch({ effects: compartments.language.reconfigure(ext) })
    }).catch(() => {})
    return () => { cancelled = true }
  }, [language, compartments])

  useEffect(() => {
    viewRef.current?.dispatch({
      effects: [
        compartments.readOnly.reconfigure(readOnlyExt(isReadOnly)),
        compartments.intel.reconfigure(intelExtensions(language, intelEnv, { complete: !isReadOnly })),
      ],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intelEnv reads codeIntel through a ref
  }, [isReadOnly, language, compartments])

  // Scroll: restore once content is there, and whenever a host asks (conflict overlay, previewer close).
  const restoredRef = useRef(false)
  // #249: reveal a requested line once the document has loaded and the editor is visible. Runs before the
  // restore below and marks it done, so the restored scroll position never lands over the revealed line.
  const cancelFlashRef = useRef<(() => void) | null>(null)
  /** A request deferred because the document was empty (see the update listener). */
  const waitingRevealSeq = useRef<number | null>(null)
  useEffect(() => () => cancelFlashRef.current?.(), [])
  useEffect(() => {
    const view = viewRef.current
    if (!revealLine || !view || isActive === false || revealedSeqs.has(revealLine.seq)) return
    syncFromHost()
    if (view.state.doc.length === 0) { waitingRevealSeq.current = revealLine.seq; return } // not loaded yet: `value` moving runs this again
    waitingRevealSeq.current = null
    revealedSeqs.add(revealLine.seq)
    restoredRef.current = true
    view.requestMeasure()
    cancelFlashRef.current?.()
    cancelFlashRef.current = revealLineIn(view, revealLine.line)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per request, and again while waiting for the text
  }, [revealLine?.seq, value, isActive])
  useEffect(() => {
    if (restoredRef.current || !value) return
    restoredRef.current = true
    restoreScroll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  const trigger = (scrollSyncTrigger ?? 0) + (viewSyncTrigger ?? 0)
  useEffect(() => {
    if (trigger) restoreScroll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  // A tab that becomes visible: CodeMirror measured nothing while it was display:none.
  useEffect(() => {
    if (!isActive) return
    const view = viewRef.current
    if (!view) return
    view.requestMeasure()
    requestAnimationFrame(() => {
      // Don't steal focus from the explorer sidebar (breaks copy/paste there).
      const active = document.activeElement
      if (!active || !active.closest('[data-workspace-explorer]')) view.focus()
    })
  }, [isActive])

  return <div ref={parentRef} className="relative flex-1 min-h-0 overflow-hidden code-surface" data-editor-engine="codemirror" />
})
